// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * DOM construction and the shared chrome: header/nav, the four workflow panels,
 * modals, the event-log drawer, glossary tooltips and toasts. Builds the tree
 * once and hands `main.ts` a bag of element references plus a few helpers.
 */

import { GLOSSARY } from './glossary';
import { mountEventDrawer } from './eventlog';
import { howItWorksHtml, privacyHtml, aboutHtml } from './modals';

export type AppState = 'start' | 'live' | 'analyzing' | 'report';

export interface AppRefs {
  root: HTMLElement;
  // start
  panelStart: HTMLElement;
  instrumentStart: HTMLElement;
  startMicBtn: HTMLButtonElement;
  tuneByEarBtn: HTMLButtonElement;
  dropzone: HTMLElement;
  fileInput: HTMLInputElement;
  sampleBtn: HTMLButtonElement;
  unsupportedNote: HTMLElement;
  // live
  panelLive: HTMLElement;
  readoutNote: HTMLElement;
  readoutOct: HTMLElement;
  readoutCents: HTMLElement;
  readoutHz: HTMLElement;
  readoutStatus: HTMLElement;
  dialCanvas: HTMLCanvasElement;
  liveMicBtn: HTMLButtonElement;
  micNote: HTMLElement;
  instrumentLive: HTMLElement;
  refGroup: HTMLElement;
  accidentalBtn: HTMLButtonElement;
  stringRow: HTMLElement;
  stringRowLabel: HTMLElement;
  stopToneBtn: HTMLButtonElement;
  downloadWavBtn: HTMLButtonElement;
  backBtn: HTMLButtonElement;
  // analyzing
  panelAnalyzing: HTMLElement;
  progressBar: HTMLElement;
  progressLabel: HTMLElement;
  // report
  panelReport: HTMLElement;
  reportImg: HTMLImageElement;
  reportSummary: HTMLElement;
  dlPngBtn: HTMLButtonElement;
  dlCsvBtn: HTMLButtonElement;
  copySummaryBtn: HTMLButtonElement;
  copyImgBtn: HTMLButtonElement;
  shareBtn: HTMLButtonElement;
  newBtn: HTMLButtonElement;
  // chrome
  drawer: HTMLElement;
  drawerToggle: HTMLButtonElement;
  // helpers
  setState: (s: AppState) => void;
  openModal: (title: string, html: string) => void;
  closeModal: () => void;
  showToast: (msg: string) => void;
  isModalOpen: () => boolean;
  isDrawerOpen: () => boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

export function buildApp(root: HTMLElement): AppRefs {
  root.innerHTML = TEMPLATE;
  const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => root.querySelector(sel) as T;

  const drawer = $('#drawer');
  const modalHost = $('#modal-host');
  const glossTip = $('#gloss-tip');
  const toastEl = $('#toast');

  // ── Modal system ──────────────────────────────────────────────────────────
  let modalOpen = false;
  const openModal = (title: string, html: string): void => {
    modalHost.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-card" role="dialog" aria-modal="true" aria-label="${escapeAttr(title)}" tabindex="-1">
        <div class="modal-head">
          <h2>${escapeHtml(title)}</h2>
          <button type="button" class="modal-close" aria-label="Close">&times;</button>
        </div>
        <div class="modal-body">${html}</div>
      </div>`;
    modalHost.hidden = false;
    modalOpen = true;
    modalHost.querySelector('.modal-close')?.addEventListener('click', closeModal);
    modalHost.querySelector('.modal-backdrop')?.addEventListener('click', closeModal);
    wireGlossary(modalHost);
    (modalHost.querySelector('.modal-card') as HTMLElement)?.focus?.();
  };
  const closeModal = (): void => {
    modalHost.hidden = true;
    modalHost.innerHTML = '';
    modalOpen = false;
  };

  // ── Toast ─────────────────────────────────────────────────────────────────
  let toastTimer: ReturnType<typeof setTimeout> | null = null;
  const showToast = (msg: string): void => {
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('show'), 2600);
  };

  // ── Glossary tooltip ──────────────────────────────────────────────────────
  const wireGlossary = (scope: HTMLElement): void => {
    scope.querySelectorAll<HTMLElement>('.gloss').forEach((el) => {
      if (el.dataset.wired) return;
      el.dataset.wired = '1';
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        const term = el.dataset.term ?? '';
        const def = GLOSSARY[term];
        if (!def) return;
        glossTip.textContent = def;
        glossTip.hidden = false;
        const r = el.getBoundingClientRect();
        const tipW = Math.min(320, window.innerWidth - 24);
        glossTip.style.width = `${tipW}px`;
        let left = r.left + r.width / 2 - tipW / 2;
        left = Math.max(12, Math.min(left, window.innerWidth - tipW - 12));
        glossTip.style.left = `${left}px`;
        glossTip.style.top = `${r.bottom + 8 + window.scrollY}px`;
      });
    });
  };
  const hideGloss = (): void => {
    glossTip.hidden = true;
  };
  document.addEventListener('click', (e) => {
    if (!(e.target as HTMLElement)?.classList?.contains('gloss')) hideGloss();
  });
  window.addEventListener('scroll', hideGloss, { passive: true });

  // ── Drawer ────────────────────────────────────────────────────────────────
  let drawerOpen = false;
  const drawerToggle = $('#drawer-toggle') as HTMLButtonElement;
  const openDrawer = (): void => {
    drawer.hidden = false;
    drawer.classList.add('open');
    drawerToggle.setAttribute('aria-expanded', 'true');
    drawerOpen = true;
  };
  const closeDrawer = (): void => {
    drawer.classList.remove('open');
    drawer.hidden = true;
    drawerToggle.setAttribute('aria-expanded', 'false');
    drawerOpen = false;
  };
  drawerToggle.addEventListener('click', () => (drawerOpen ? closeDrawer() : openDrawer()));
  mountEventDrawer($('#drawer-content'), closeDrawer);

  // ── Nav modal buttons ─────────────────────────────────────────────────────
  $('#nav-how').addEventListener('click', () => openModal('How Tunewell works', howItWorksHtml()));
  $('#nav-privacy').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));
  $('#nav-about').addEventListener('click', () => openModal('About Tunewell', aboutHtml()));
  $('#trust-banner').addEventListener('click', () => openModal('Privacy & threat model', privacyHtml()));

  wireGlossary(root);

  // ── State switching ───────────────────────────────────────────────────────
  const panels: Record<AppState, HTMLElement> = {
    start: $('#panel-start'),
    live: $('#panel-live'),
    analyzing: $('#panel-analyzing'),
    report: $('#panel-report'),
  };
  const setState = (s: AppState): void => {
    for (const key of Object.keys(panels) as AppState[]) panels[key].hidden = key !== s;
    root.dataset.state = s;
  };
  setState('start');

  return {
    root,
    panelStart: panels.start,
    instrumentStart: $('#instrument-start'),
    startMicBtn: $('#start-mic'),
    tuneByEarBtn: $('#tune-by-ear'),
    dropzone: $('#dropzone'),
    fileInput: $('#file-input'),
    sampleBtn: $('#sample-btn'),
    unsupportedNote: $('#unsupported-note'),
    panelLive: panels.live,
    readoutNote: $('#readout-note'),
    readoutOct: $('#readout-oct'),
    readoutCents: $('#readout-cents'),
    readoutHz: $('#readout-hz'),
    readoutStatus: $('#readout-status'),
    dialCanvas: $('#dial-canvas'),
    liveMicBtn: $('#live-mic'),
    micNote: $('#mic-note'),
    instrumentLive: $('#instrument-live'),
    refGroup: $('#ref-group'),
    accidentalBtn: $('#accidental-btn'),
    stringRow: $('#string-row'),
    stringRowLabel: $('#string-row-label'),
    stopToneBtn: $('#stop-tone'),
    downloadWavBtn: $('#download-wav'),
    backBtn: $('#back-btn'),
    panelAnalyzing: panels.analyzing,
    progressBar: $('#progress-bar'),
    progressLabel: $('#progress-label'),
    panelReport: panels.report,
    reportImg: $('#report-img'),
    reportSummary: $('#report-summary'),
    dlPngBtn: $('#dl-png'),
    dlCsvBtn: $('#dl-csv'),
    copySummaryBtn: $('#copy-summary'),
    copyImgBtn: $('#copy-img'),
    shareBtn: $('#share-btn'),
    newBtn: $('#new-btn'),
    drawer,
    drawerToggle,
    setState,
    openModal,
    closeModal,
    showToast,
    isModalOpen: () => modalOpen,
    isDrawerOpen: () => drawerOpen,
    openDrawer,
    closeDrawer,
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}

const TEMPLATE = /* html */ `
  <header class="site-header">
    <div class="brand">
      <span class="brand-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8 3v9a4 4 0 0 0 8 0V3" /><path d="M12 16v5" />
        </svg>
      </span>
      <span class="brand-name">Tunewell</span>
    </div>
    <nav class="nav">
      <button type="button" id="nav-how" class="nav-btn">How it works</button>
      <button type="button" id="nav-privacy" class="nav-btn">Privacy</button>
      <button type="button" id="nav-about" class="nav-btn">About</button>
      <button type="button" id="drawer-toggle" class="nav-btn nav-icon" aria-expanded="false" aria-label="Event log">Log</button>
    </nav>
  </header>

  <button type="button" id="trust-banner" class="trust-banner">
    <span class="dot"></span> Runs entirely in your browser. Audio never leaves your device. <span class="trust-more">Details</span>
  </button>

  <main class="main-content">
    <!-- START -->
    <section id="panel-start" class="panel panel-start">
      <h1 class="hero-title">Instrument tuner</h1>
      <p class="hero-sub">Tune a guitar, bass, ukulele or violin with your microphone — or by ear with a
      reference tone you can download. It shows the note and how many <span class="gloss" data-term="cents">cents</span>
      sharp or flat you are. Nothing is uploaded.</p>

      <div id="instrument-start" class="seg seg-wrap" role="group" aria-label="Instrument"></div>

      <div class="start-actions">
        <button type="button" id="start-mic" class="btn btn-primary btn-lg">Start microphone</button>
        <button type="button" id="tune-by-ear" class="btn btn-lg btn-ghost">Tune by ear</button>
      </div>

      <p class="or-line">or check a recording</p>
      <div id="dropzone" class="dropzone" tabindex="0" role="button" aria-label="Choose an audio file to analyse">
        <div class="dz-inner">
          <div class="dz-title">Analyse a recording's pitch</div>
          <div class="dz-sub">Drop an audio clip here, or tap to choose — MP3, M4A, WAV, OGG, FLAC</div>
          <button type="button" id="sample-btn" class="pill">Try a sample</button>
        </div>
        <input type="file" id="file-input" accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac" hidden />
      </div>
      <p id="unsupported-note" class="note" hidden></p>
    </section>

    <!-- LIVE -->
    <section id="panel-live" class="panel panel-live" hidden>
      <div class="readout">
        <div class="readout-note-wrap">
          <span id="readout-note" class="readout-note">–</span><span id="readout-oct" class="readout-oct"></span>
        </div>
        <div id="readout-cents" class="readout-cents">Listening…</div>
      </div>

      <canvas id="dial-canvas" class="dial-canvas" aria-hidden="true"></canvas>
      <div class="readout-sub">
        <span id="readout-hz" class="readout-hz">— Hz</span>
        <span id="readout-status" class="readout-status"></span>
      </div>

      <div class="controls">
        <div class="control control-wide">
          <label>Instrument</label>
          <div id="instrument-live" class="seg seg-wrap" role="group" aria-label="Instrument"></div>
        </div>
        <div class="control">
          <label><span class="gloss" data-term="reference-a">Reference A</span></label>
          <div id="ref-group" class="seg" role="group" aria-label="Reference A"></div>
        </div>
        <div class="control">
          <label>Names</label>
          <button type="button" id="accidental-btn" class="seg-btn seg-solo" aria-label="Toggle sharps or flats">♯ sharps</button>
        </div>
      </div>

      <div class="pipe">
        <div id="string-row-label" class="pipe-label">Reference tones — tap to hear</div>
        <div id="string-row" class="string-row"></div>
        <div class="pipe-actions">
          <button type="button" id="stop-tone" class="btn btn-sm" hidden>Stop tone</button>
          <button type="button" id="download-wav" class="btn btn-sm">Download tones (WAV)</button>
        </div>
      </div>

      <div class="live-actions">
        <button type="button" id="live-mic" class="btn btn-record">Start microphone</button>
        <button type="button" id="back-btn" class="btn btn-ghost">Done</button>
      </div>
      <p id="mic-note" class="note note-muted" hidden></p>
    </section>

    <!-- ANALYZING -->
    <section id="panel-analyzing" class="panel panel-analyzing" hidden>
      <div class="analyzing-inner">
        <div id="progress-label" class="progress-label">Analysing…</div>
        <div class="progress-track"><div id="progress-bar" class="progress-bar"></div></div>
      </div>
    </section>

    <!-- REPORT -->
    <section id="panel-report" class="panel panel-report" hidden>
      <div class="report-preview">
        <img id="report-img" alt="Pitch-over-time chart" />
      </div>
      <div id="report-summary" class="report-summary"></div>
      <div class="report-actions">
        <button type="button" id="dl-png" class="btn btn-primary">Download report (PNG)</button>
        <button type="button" id="dl-csv" class="btn">Download CSV</button>
        <button type="button" id="copy-summary" class="btn">Copy summary</button>
        <button type="button" id="copy-img" class="btn">Copy image</button>
        <button type="button" id="share-btn" class="btn" hidden>Share</button>
        <button type="button" id="new-btn" class="btn btn-ghost">New</button>
      </div>
    </section>
  </main>

  <footer class="site-footer">
    <div class="footer-inner">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
    </div>
  </footer>

  <aside id="drawer" class="drawer" hidden aria-label="Event log">
    <div id="drawer-content"></div>
  </aside>

  <div id="modal-host" class="modal-host" hidden></div>
  <div id="gloss-tip" class="gloss-tip" role="tooltip" hidden></div>
  <div id="toast" class="toast" role="status" aria-live="polite"></div>
`;
